import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { DataMartConstants } from '@r1-compass-library/constants/data-mart.constants';
import { Environment } from '@r1-compass-library/environments/environment';
import { DataMartPicklistValue, DivisionTableRelationships } from '@r1-compass-library/models';
import {
    DataMartCmtResponse,
    DataMartCmtSearchResponse,
    DataMartDataModelResponse,
    DataMartMergeFieldResponse,
    DataMartMergeFieldSearchResponse,
    DataMartMergeFieldTableResponse,
    DataMartMultipleRecommendationsTableMapResponse,
    DataMartPicklistResponse,
    DataMartQueryCountResponse,
    DataMartSegmentationCriteriaResponse,
    DataMartTypeaheadSearchResponse,
    DataMartWebSearchResponse,
} from '@r1-compass-library/models/data-mart/data-mart-response.model';
import { ErrorHandlingService } from '@r1-compass-library/services/error-handling.service';
import { catchError, finalize, map, Observable, of, retry } from 'rxjs';
import { SpinnerService } from './spinner.service';

@Injectable({ providedIn: 'root' })
export class DataMartService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);
    readonly #errorHandlingService = inject(ErrorHandlingService);

    private BASE_URL: string = Environment.baseUrl + '/datamart';

    private DEFAULT_SEARCH_LIMIT = 1000;

    getDataModel(additionalHeaders: any = {}): Observable<DataMartDataModelResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };
        const process = 'DataMartService.getDataModel';
        this.#spinnerService.addProcess(process, 'Getting DataMart data model...');
        return this.#http.get<DataMartDataModelResponse>(`${this.BASE_URL}/segmentation/datamodel`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartDataModelResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getPicklist(tableName: string, fieldName: string): Observable<DataMartPicklistResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(DataMartConstants.QueryParams.TABLE_NAME, tableName)
                .set(DataMartConstants.QueryParams.FIELD_NAME, fieldName),
        };

        const process = `DataMartService.getPicklist.${tableName}.${fieldName}`;
        this.#spinnerService.addProcess(process, 'Getting DataMart picklist...');
        return this.#http.get<DataMartPicklistResponse>(`${this.BASE_URL}/metadata/picklist`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartPicklistResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    typeaheadSearch(
        tableName: string,
        fieldName: string,
        searchTerm: string,
        limit?: number
    ): Observable<DataMartTypeaheadSearchResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(DataMartConstants.QueryParams.TABLE_NAME, tableName)
                .set(DataMartConstants.QueryParams.FIELD_NAME, fieldName)
                .set(DataMartConstants.QueryParams.SEARCH_TERM, searchTerm)
                .set(DataMartConstants.QueryParams.LIMIT, limit ? limit : this.DEFAULT_SEARCH_LIMIT),
        };

        const process = `DataMartService.typeaheadSearch.${tableName}.${fieldName}`;
        this.#spinnerService.addProcess(process, 'Searching DataMart...');
        return this.#http.get<DataMartTypeaheadSearchResponse>(`${this.BASE_URL}/search/typeahead`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartTypeaheadSearchResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getMergeFields(tableName?: string): Observable<DataMartMergeFieldResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        if (tableName) {
            httpOptions.params = httpOptions.params.set(DataMartConstants.QueryParams.TABLE_NAME, tableName);
        }

        const process = `DataMartService.getMergeFields.${tableName}`;
        this.#spinnerService.addProcess(process, 'Getting DataMart merge fields...');
        return this.#http.get<DataMartMergeFieldResponse>(`${this.BASE_URL}/talkingpoint/mergefield`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartMergeFieldResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    searchMergeFields(searchTerm: string, offset: number): Observable<DataMartMergeFieldSearchResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams().set('searchTerm', searchTerm).set('offset', offset),
        };

        const process = `DataMartService.searchMergeFields`;
        this.#spinnerService.addProcess(process, 'Getting DataMart merge fields...');
        return this.#http
            .get<DataMartMergeFieldSearchResponse>(`${this.BASE_URL}/talkingpoint/search`, httpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of<DataMartMergeFieldSearchResponse>();
                }),
                finalize(() => this.#spinnerService.completeProcess(process))
            );
    }

    getMergeFieldTables(): Observable<DataMartMergeFieldTableResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        const process = 'DataMartService.getMergeFieldTables';
        this.#spinnerService.addProcess(process, 'Getting DataMart merge field tables...');
        return this.#http
            .get<DataMartMergeFieldTableResponse>(`${this.BASE_URL}/talkingpoint/mergefieldtable`, httpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of<DataMartMergeFieldTableResponse>();
                }),
                finalize(() => this.#spinnerService.completeProcess(process))
            );
    }

    getCmts(level: number): Observable<DataMartCmtResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams().set(DataMartConstants.QueryParams.LEVEL, level),
        };

        const process = 'DataMartService.getCmts';
        this.#spinnerService.addProcess(process, 'Getting DataMart CMT values...');
        return this.#http.get<DataMartCmtResponse>(`${this.BASE_URL}/metadata/cmt`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartCmtResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    cmtSearch(level: number, searchTerm: string, limit?: number): Observable<DataMartCmtSearchResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(DataMartConstants.QueryParams.LEVEL, level)
                .set(DataMartConstants.QueryParams.SEARCH_TERM, searchTerm)
                .set(DataMartConstants.QueryParams.LIMIT, limit ? limit : this.DEFAULT_SEARCH_LIMIT),
        };

        const process = `DataMartService.cmtSearch.${level}.${searchTerm}`;
        this.#spinnerService.addProcess(process, 'Searching DataMart CMT values...');
        return this.#http.get<DataMartCmtSearchResponse>(`${this.BASE_URL}/search/cmtsearch`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartCmtSearchResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    webSearch(searchTerm: string, limit?: number): Observable<DataMartWebSearchResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(DataMartConstants.QueryParams.SEARCH_TERM, searchTerm)
                .set(DataMartConstants.QueryParams.LIMIT, limit ? limit : this.DEFAULT_SEARCH_LIMIT),
        };

        const process = `DataMartService.webSearch.${searchTerm}`;
        this.#spinnerService.addProcess(process, 'Searching DataMart web search values...');
        return this.#http.get<DataMartWebSearchResponse>(`${this.BASE_URL}/search/websearch`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartWebSearchResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getQueryCount(query: string): Observable<DataMartQueryCountResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const body = { [DataMartConstants.QueryParams.QUERY]: query };

        const url = `${this.BASE_URL}/querycount/qc`;

        const process = 'DataMartService.getQueryCount';
        this.#spinnerService.addProcess(process, 'Getting DataMart query count...');
        return this.#http.post<DataMartQueryCountResponse>(url, body, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return this.#errorHandlingService.handleDataMartApiError(error);
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getMultipleRecommendationsQueryCount(query: string): Observable<DataMartQueryCountResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const body = { [DataMartConstants.QueryParams.QUERY]: query };

        const url = `${this.BASE_URL}/querycount/mrqc`;

        const process = 'DataMartService.getMultipleRecommendationsQueryCount';
        this.#spinnerService.addProcess(process, 'Getting DataMart query count...');
        return this.#http.post<DataMartQueryCountResponse>(url, body, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return this.#errorHandlingService.handleDataMartApiError(error);
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getRelations(
        sourceTableName: string,
        sourceColumnName: string
    ): Observable<DivisionTableRelationships | undefined> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(DataMartConstants.QueryParams.TABLE_NAME, sourceTableName)
                .set(DataMartConstants.QueryParams.FIELD_NAME, sourceColumnName),
        };

        const process = `DataMartService.getRelations.${sourceTableName}.${sourceColumnName}`;
        this.#spinnerService.addProcess(process, 'Getting DataMart table relations...');
        return this.#http.get<DivisionTableRelationships>(`${this.BASE_URL}/relations/tables`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of(undefined);
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getLifecycleDataModel(): Observable<DataMartDataModelResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'DataMartService.getLifecycleDateModel';
        this.#spinnerService.addProcess(process, 'Getting DataMart lifecycle data model...');
        return this.#http.get<DataMartDataModelResponse>(`${this.BASE_URL}/metadata/lifecycle/rules`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<DataMartDataModelResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getSelfManufacturedOptions(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistorySelfMfgColumn
    ): Observable<DataMartPicklistValue[]> {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistorySelfMfgColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getSalesHistoryTargetLevels(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistoryCmtColumn
    ): Observable<DataMartPicklistValue[]> {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistoryCmtColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getWebActivityTargetLevels(
        tableName: string = DataMartConstants.DefaultWebActivityTable,
        columnName: string = DataMartConstants.DefaultWebActivityCmtColumn
    ) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultWebActivityTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultWebActivityCmtColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getSspOptions(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistorySspColumn
    ) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistorySspColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getPrivateLabelOptions(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistoryProductLabelColumn
    ) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistoryProductLabelColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getSalesHistoryChannels(
        tableName: string = DataMartConstants.DefaultSalesHistoryTable,
        columnName: string = DataMartConstants.DefaultSalesHistoryChannelsColumn
    ) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        if (!columnName) {
            columnName = DataMartConstants.DefaultSalesHistoryChannelsColumn;
        }
        return this.getPicklist(tableName, columnName).pipe(
            map(response => response.entries),
            retry(0),
            catchError(() => {
                return of<DataMartPicklistValue[]>();
            })
        );
    }

    getSalesHistoryMetrics(tableName: string = DataMartConstants.DefaultSalesHistoryTable) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultSalesHistoryTable;
        }
        return this.getDataModel().pipe(
            map(dataModel => {
                const table = dataModel.tables.find(tbl => tbl.name === tableName);
                return table?.fields.filter(field => field.fieldType === 'Metric') ?? [];
            })
        );
    }

    getWebActivityPeriods(tableName: string = DataMartConstants.DefaultWebActivityTable) {
        if (!tableName) {
            tableName = DataMartConstants.DefaultWebActivityTable;
        }
        return this.getDataModel().pipe(
            map(dataModel => {
                const table = dataModel.tables.find(tbl => tbl.name === tableName);
                return table?.fields.filter(field => field.fieldType === 'Web Activity Period') ?? [];
            })
        );
    }

    /**
     * @description In R1, this hits `mocks` table.
     */
    getSegmentationCriteria() {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        const process = 'DataMartService.getSegmentationCriteria';
        this.#spinnerService.addProcess(process, 'Getting DataMart Segmentation Criteria...');
        return this.#http
            .get<DataMartSegmentationCriteriaResponse>(`${this.BASE_URL}/segmentation/criteria`, httpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of<DataMartSegmentationCriteriaResponse>();
                }),
                finalize(() => this.#spinnerService.completeProcess(process))
            );
    }

    getMultipleRecommendationsTableMap(): Observable<DataMartMultipleRecommendationsTableMapResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };

        const process = 'DataMartService.getMultipleRecommendationsTableMap';
        this.#spinnerService.addProcess(process, 'Getting DataMart Multiple Recommendations Table Map...');
        return this.#http
            .get<DataMartMultipleRecommendationsTableMapResponse>(
                `${this.BASE_URL}/relations/multirectables`,
                httpOptions
            )
            .pipe(
                retry(0),
                catchError(() => {
                    return of<DataMartMultipleRecommendationsTableMapResponse>();
                }),
                finalize(() => this.#spinnerService.completeProcess(process))
            );
    }
}
